/**
 * 商品详情页跳转工具
 *
 * 背景：各详情页读取商品 ID 的实现不统一（有的只认 location.state、
 * 有的只认 URL query），导致从不同入口跳转时会出现「拿不到 ID → 空白页」
 * 甚至「静默展示 1 号商品」的问题。
 *
 * 统一约定：
 *   1. 跳转方【同时】写 query 与 state
 *      - query：刷新页面/分享链接后依然可用（持久）
 *      - state：首屏可携带完整商品对象，少一次请求（易失）
 *   2. 详情页读取时【两者都查】，query 优先于 state 的兜底逻辑保持一致
 */

/** 商品类型 → 详情页路径 + ID 参数名 */
export const DETAIL_ROUTE: Record<
  string,
  { path: string; param: string }
> = {
  tour: { path: "/route-detail-product", param: "routeId" },
  guide: { path: "/guide-detail-product", param: "guideId" },
  hotel: { path: "/hotel-detail-product", param: "hotelId" },
  car: { path: "/car-detail-product", param: "carId" },
};

/**
 * 按商品类型生成详情页跳转地址（只含 query，state 由调用方按需附加）
 *
 * @param type   商品类型（tour / guide / hotel / car）
 * @param id     商品 ID
 * @returns      完整路径，如「/route-detail-product?routeId=31」；类型未知时返回 null
 */
export function buildDetailPath(
  type: string | undefined,
  id: number | string | undefined | null,
): string | null {
  if (id === undefined || id === null || id === "") return null;
  const conf = type ? DETAIL_ROUTE[type] : undefined;
  if (!conf) return null;
  return `${conf.path}?${conf.param}=${id}`;
}

/**
 * 从 URL query 与 location.state 中解析商品 ID（详情页统一使用）
 *
 * 读取顺序：query（持久，刷新不丢）→ state（首屏携带）
 * 返回 undefined 表示两处都没有，调用方需自行处理「无 ID」场景。
 *
 * @param paramName 参数名，如 "routeId"
 * @param search    URLSearchParams 实例
 * @param state     react-router 的 location.state
 */
export function resolveDetailId(
  paramName: string,
  search: URLSearchParams,
  state: unknown,
): number | undefined {
  const fromQuery = Number(search.get(paramName));
  if (Number.isFinite(fromQuery) && fromQuery > 0) return fromQuery;

  const fromState = Number((state as Record<string, unknown>)?.[paramName]);
  if (Number.isFinite(fromState) && fromState > 0) return fromState;

  return undefined;
}
